import { MessageCircle, Phone, Check } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { PHONE_TEL, waLink } from "@/lib/contact";

const PHYSIO_IMG = "https://images.pexels.com/photos/20860610/pexels-photo-20860610.jpeg";

const includes = [
  "45-minute video assessment",
  "Movement & posture analysis",
  "Personalised rehab plan",
  "Guided exercise demonstrations",
  "Progress reviews every two weeks",
];

const steps = [
  { n: "01", title: "Book on WhatsApp", text: "Message us your concern and pick a time that suits you." },
  { n: "02", title: "Video assessment", text: "A full movement screen and diagnosis over a video call." },
  { n: "03", title: "Your rehab plan", text: "A personalised program with demo videos, built for your body." },
  { n: "04", title: "Review & progress", text: "Regular follow-ups to keep you moving forward, pain-free." },
];

export default function Physio() {
  return (
    <section id="physiotherapy" className="py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-10 items-start">
          <div className="lg:col-span-5 relative order-2 lg:order-1">
            <Reveal>
              <div className="relative">
                <div className="absolute -top-5 -left-5 w-full h-full border border-[#8DA37F]/50" />
                <div className="relative overflow-hidden aspect-[4/5]">
                  <img
                    src={PHYSIO_IMG}
                    alt="Physiotherapist guiding a patient through treatment"
                    className="w-full h-full object-cover sepia-[0.12]"
                    data-testid="physio-image"
                  />
                </div>
                <div className="absolute -bottom-6 right-6 bg-[#111111] text-[#F5F0E8] px-6 py-5">
                  <p className="font-display text-2xl italic leading-none">No clinic queues.</p>
                  <p className="text-xs tracking-[0.15em] uppercase mt-2 text-[#F5F0E8]/60">
                    100% online care
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2 lg:pl-8">
            <Reveal>
              <SectionLabel>Physiotherapy Consultation</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light text-[#111111] text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight mt-6">
                Expert physiotherapy,
                <br />
                <span className="italic text-[#8DA37F]">without the waiting room.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-[#111111]/70">
                From post-surgical rehab to stubborn aches that never quite left —
                get a precise diagnosis and a plan that fits your life, over a
                simple video call.
              </p>
            </Reveal>

            <Reveal delay={0.24}>
              <ul className="mt-9 space-y-4">
                {includes.map((item) => (
                  <li key={item} className="flex items-center gap-4 text-[#111111]/85">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#8DA37F]/20">
                      <Check className="h-3.5 w-3.5 text-[#6b7f5e]" />
                    </span>
                    <span className="text-base md:text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={waLink("Hi Physiora, I'd like to book a physiotherapy consultation.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="physio-whatsapp-btn"
                  className="inline-flex items-center gap-3 bg-[#111111] text-[#F5F0E8] text-xs tracking-[0.15em] uppercase font-medium px-8 py-4 rounded-full hover:bg-[#8DA37F] hover:text-[#111111] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <MessageCircle className="h-4 w-4" />
                  Book via WhatsApp
                </a>
                <a
                  href={PHONE_TEL}
                  data-testid="physio-call-btn"
                  className="inline-flex items-center gap-3 text-xs tracking-[0.15em] uppercase font-medium px-8 py-4 rounded-full border border-[#111111]/25 text-[#111111] hover:border-[#111111] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Phone className="h-4 w-4" />
                  Call now
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 md:mt-32 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E0D8] border border-[#E5E0D8]">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className="bg-[#F5F0E8]">
              <div className="p-8 md:p-10 h-full group hover:bg-[#efe9dd] transition-colors duration-500">
                <p className="font-display text-4xl italic text-[#8DA37F]">{s.n}</p>
                <h3 className="mt-5 text-lg font-medium text-[#111111]">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#111111]/60">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
